/** @jsx isolateComponent */
import {
  Colors,
  Icon,
  Spacer,
  TagLabel,
  Text,
  FloatingBox,
} from '@zapier/design-system';
import InAppSidebarSubheaderButton from './InAppSidebarSubheaderButton';
import { Fragment, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { isolateComponent } from '@zapier/style-encapsulation';
import { useUniversalNavContext } from '../../../hooks/useUniversalNavContext';
import { SidebarV3 } from './types';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';

const MenuStyles = ({ topbarSize }: { topbarSize: 'medium' | 'small' }) =>
  css({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: 'inherit',
    border: '1px solid transparent',
    boxSizing: 'border-box',
    overflow: 'auto',
    padding: '10px',
    width: topbarSize === 'medium' ? '275px' : '260px',
    left: '3px',
    top: '30px',
    minWidth: '150px',
    outline: 'none',
  });

const MenuItemStyles = css({
  display: 'flex',
  gap: '10px',
  padding: '0.286rem 0.571rem',
  borderRadius: '6px',
  outline: 'none',
  cursor: 'pointer',
  fontSize: '1.072rem',
  position: 'relative',
  alignItems: 'center',
  columnGap: '10px',
  color: 'inherit',
  textDecoration: 'none',

  '&:focus, &:hover': {
    backgroundColor: Colors.cream,
  },
  '& span': {
    transition: 'none',
  },
});

type CreationItemProps = SidebarV3.CreateOption;

const CreationItem = ({
  description,
  href,
  icon,
  iconFilled,
  id,
  tag,
  target,
  title,
}: CreationItemProps) => {
  const [isActive, setIsActive] = useState(false);
  const { getFullyQualifiedURL } = useUniversalNavContext();

  return (
    <a
      css={MenuItemStyles}
      href={getFullyQualifiedURL(href)}
      id={id}
      target={target}
      onFocus={() => setIsActive(true)}
      onBlur={() => setIsActive(false)}
      onMouseEnter={() => setIsActive(true)}
      onMouseLeave={() => setIsActive(false)}
      onClick={() => {
        emitUserInteractionEvent({
          event_action: 'click',
          option_selected: `UniversalSidebar CreateDropdown: ${title}`,
        });
      }}
    >
      <Icon
        name={isActive ? iconFilled : icon}
        size={28}
        color={isActive ? 'brand' : 'neutral800'}
      />
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div css={{ display: 'flex', alignItems: 'center' }}>
          <Text type="paragraph3Medium">{title}</Text>
          {tag && (
            <Fragment>
              <Spacer width={10} />
              <TagLabel size="small" color="lavender">
                {tag}
              </TagLabel>
            </Fragment>
          )}
        </div>
        <Text type="smallPrint1">{description}</Text>
      </div>
    </a>
  );
};

type CreateDropdownProps = {
  isCollapsed: boolean;
  items: SidebarV3.CreateOption[];
};

export const CreateDropdown = ({ isCollapsed, items }: CreateDropdownProps) => {
  const { isCreateMenuOpen, setIsCreateMenuOpen, topbarSize } =
    useUniversalNavContext();
  const buttonRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={buttonRef} style={{ position: 'relative' }}>
      <InAppSidebarSubheaderButton
        iconName="formAdd"
        isCollapsed={isCollapsed}
        onClick={(event) => {
          // Stopping proppagation in order to keep the sidebar from closing when clicking on the Create `button` element.
          event.stopPropagation();

          emitUserInteractionEvent({
            event_action: 'click',
            option_selected: `UniversalSidebar CreateDropdown ${
              isCreateMenuOpen ? 'close' : 'open'
            }`,
          });

          setIsCreateMenuOpen(!isCreateMenuOpen);
        }}
        text="Create"
      />

      {isCreateMenuOpen && (
        <FloatingBox
          borderColor="transparent"
          borderRadius={10}
          onClickOutside={(event) => {
            // exclude the button from this click handler
            if (
              buttonRef &&
              buttonRef.current &&
              !buttonRef.current.contains(event.target as HTMLElement)
            )
              setIsCreateMenuOpen(false);
          }}
        >
          <div css={MenuStyles({ topbarSize })}>
            {items.map((item) => (
              <CreationItem {...item} key={item.id} />
            ))}
          </div>
        </FloatingBox>
      )}
    </div>
  );
};
